// src/AuthSection.js
import React, { useState } from "react";
import "./Register.css"; // reuse same styles

const BWC_BASE_URL = "https://bwc-prod.innovateherhacks.org";

function LoginForm({ onLogin, onShowRegister }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(
        BWC_BASE_URL + "/api/v1/lx/auth/login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: email,
            password: password,
          }),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error("Login failed: " + res.status + " " + text);
      }

      const data = await res.json(); // { access_token, token_type }
      setSuccess("Login successful!");
      if (onLogin) onLogin(data.access_token);
    } catch (err) {
      setError(err.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="register-text">
        <h1>LOGIN</h1>
        <p>
          Log in to your InnovateHer account to view event information and
          participate in the hackathon.
        </p>
      </div>

      <form className="register-form" onSubmit={handleSubmit}>
        <label className="register-field">
          <span>Email</span>
          <input
            type="email"
            name="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="register-field">
          <span>Password</span>
          <input
            type="password"
            name="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        <button
          type="submit"
          className="register-button"
          disabled={loading}
        >
          {loading ? "Logging in..." : "Log In"}
        </button>

        {error && (
          <p className="register-message register-error">{error}</p>
        )}
        {success && (
          <p className="register-message register-success">{success}</p>
        )}

        <p className="register-message">
          Don&apos;t have an account?{" "}
          <button
            type="button"
            className="link-button"
            onClick={onShowRegister}
          >
            Register
          </button>
        </p>
      </form>
    </>
  );
}

function RegisterForm({ onRegister, onBackToLogin }) {
  const [form, setForm] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    pronouns: "",
    school: "",
    major: "",
    uid: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const trimmed = form.uid.trim();
      if (!trimmed) {
        throw new Error("Badge ID is required.");
      }

      // Allow optional 0x prefix
      const hexString = trimmed.toLowerCase().startsWith("0x")
        ? trimmed.slice(2)
        : trimmed;
      const uidInt = parseInt(hexString, 16);

      if (Number.isNaN(uidInt)) {
        throw new Error("Badge ID must be a valid hex number.");
      }

      const res = await fetch(
        BWC_BASE_URL + "/api/v1/lx/auth/register",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: form.email,
            password: form.password,
            first_name: form.first_name,
            last_name: form.last_name,
            pronouns: form.pronouns,
            school: form.school,
            major: form.major,
            uid: uidInt,
          }),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(
          "Registration failed: " + res.status + " " + text
        );
      }

      const data = await res.json(); // { access_token, token_type }
      setSuccess("Registration successful! You can now log in.");
      if (onRegister) onRegister(data.access_token);
    } catch (err) {
      setError(err.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="register-text">
        <h1>REGISTER</h1>
        <p>
          Create your InnovateHer account to participate in the hackathon.
        </p>
      </div>

      <form className="register-form" onSubmit={handleSubmit}>
        <label className="register-field">
          <span>Email</span>
          <input
            type="email"
            name="email"
            required
            value={form.email}
            onChange={handleChange}
          />
        </label>

        <label className="register-field">
          <span>Password</span>
          <input
            type="password"
            name="password"
            required
            value={form.password}
            onChange={handleChange}
          />
        </label>

        <label className="register-field">
          <span>First name</span>
          <input
            type="text"
            name="first_name"
            required
            value={form.first_name}
            onChange={handleChange}
          />
        </label>

        <label className="register-field">
          <span>Last name</span>
          <input
            type="text"
            name="last_name"
            required
            value={form.last_name}
            onChange={handleChange}
          />
        </label>

        <label className="register-field">
          <span>Pronouns</span>
          <input
            type="text"
            name="pronouns"
            value={form.pronouns}
            onChange={handleChange}
          />
        </label>

        <label className="register-field">
          <span>School</span>
          <input
            type="text"
            name="school"
            value={form.school}
            onChange={handleChange}
          />
        </label>

        <label className="register-field">
          <span>Major</span>
          <input
            type="text"
            name="major"
            value={form.major}
            onChange={handleChange}
          />
        </label>

        <label className="register-field">
          <span>Badge ID (UID on card, hex)</span>
          <input
            type="text"
            name="uid"
            required
            placeholder="e.g. 1A2B3C4D"
            value={form.uid}
            onChange={handleChange}
          />
        </label>

        <button
          type="submit"
          className="register-button"
          disabled={loading}
        >
          {loading ? "Registering..." : "Register"}
        </button>

        {error && (
          <p className="register-message register-error">{error}</p>
        )}
        {success && (
          <p className="register-message register-success">{success}</p>
        )}

        <button
          type="button"
          className="back-button"
          onClick={onBackToLogin}
        >
          Back to login
        </button>
      </form>
    </>
  );
}

function AuthSection({ onAccessToken }) {
  const [mode, setMode] = useState("login"); // "login" | "register"

  function handleLogin(token) {
    if (onAccessToken) onAccessToken(token);
    // you can also navigate to other sections here
  }

  function handleRegister(token) {
    // you might choose to auto-login or just let them go back to login
    if (onAccessToken) onAccessToken(token);
  }

  return (
    <div className="register-container">
      <div className="register-header-section">
        {mode === "login" ? (
          <LoginForm
            onLogin={handleLogin}
            onShowRegister={() => setMode("register")}
          />
        ) : (
          <RegisterForm
            onRegister={handleRegister}
            onBackToLogin={() => setMode("login")}
          />
        )}
      </div>
    </div>
  );
}

export default AuthSection;
